import { attachAutomaticDirtyWatcher } from "@frui.ts/dirtycheck";
import { bound } from "@frui.ts/helpers";
import { action, observable } from "mobx";
import subMinutes from "date-fns/subMinutes";
import VehicleChangeDuringValidityRequest from "models/vehicleChangeDuringValidityRequest";
import ChargeRepository from "repositories/chargeRepository";
import EshopError from "../models/eshopError";
import EventBus, {
  chargeValidationFailed,
  downloadPaymentConfirmationFailure,
  downloadPaymentConfirmationSuccess,
  emailUpdated,
  putChargeContactFailure,
  putChargeValidityFailure,
  putChargeValiditySuccess,
  putChargeVehicleFailure,
  sendChargePaymentConfirmationEmailFailure,
  sendChargePaymentConfirmationEmailSuccess,
} from "./eventBus";
import SignalRNotificationService from "./signalR/notification/signalRNotificationService";
import ChargeDetailResponse from "entities/chargeDetailResponse";
import type { ContactEditationTriggerer } from "models/notificationPreferences";
import ChargeValidityRequest from "entities/chargeValidityRequest";
import { isSameDay } from "date-fns";
import ChargeTypesService from "./chargeTypesService";
import VehicleRepository from "repositories/vehicleRepository";
import NotificationPreferences from "models/notificationPreferences";
import ContactInfoChangeRequest from "entities/contactInfoChangeRequest";
import TimeService from "./timeService";
import { isSameOrBeforePragueDay, utcToCZZonedTime } from "utils/dateUtils";
import { dateToIsoDate } from "../utils/dateUtils";
import ChargeChangeRejectionReason from "entities/chargeChangeRejectionReason";
import ChargeEmailAddressChangeRejectionReason from "entities/chargeEmailAddressChangeRejectionReason";
import ChargeNotificationPreferencesChangeRejectionReason from "entities/chargeNotificationPreferencesChangeRejectionReason";

export const DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE = "DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE";
export const SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE = "SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE";
export const PUT_CHARGE_CONTACT_REQUEST_FAILURE = "PUT_CHARGE_CONTACT_REQUEST_FAILURE";
export const PUT_CHARGE_VALIDITY_REQUEST_FAILURE = "PUT_CHARGE_VALIDITY_REQUEST_FAILURE";
export const PUT_CHARGE_VEHICLE_REQUEST_FAILURE = "PUT_CHARGE_VEHICLE_REQUEST_FAILURE";
export const WEBSOCKET_CONNECTION_FAILURE = "WEBSOCKET_CONNECTION_FAILURE";
export const CHANGE_NOT_ALLOWED_ANOTHER_CHARGE_FOR_VEHICLE_WITH_VALIDITY_OVERLAP_EXISTS =
  "CHANGE_NOT_ALLOWED_ANOTHER_CHARGE_FOR_VEHICLE_WITH_VALIDITY_OVERLAP_EXISTS";
const SESSION_STORAGE_CHARGE_MANAGEMENT_KEY = "charge_management";
const SESSION_STORAGE_CHARGE_MANAGEMENT_TIME_KEY = "charge_management_time";
export default class ChargeManagementService {
  @observable charge?: ChargeDetailResponse;
  @observable turnstileToken?: string;

  constructor(
    private repository: ChargeRepository,
    private chargeTypesService: ChargeTypesService,
    private timeService: TimeService,
    private chargeRepository: ChargeRepository,
    private vehicleRepository: VehicleRepository,
    private eventBus: EventBus,
    private signalRNotificationService: SignalRNotificationService
  ) {
    this.eventBus.subscribe(putChargeValiditySuccess, this.onChargeValidityUpdateSuccess);
  }

  @bound
  protected onChargeValidityUpdateSuccess(data: ReturnType<typeof putChargeValiditySuccess>) {
    const validSince = data.payload.validity.validSince;
    this.charge!.permittedActions.validityChange.isAllowed = false;

    // Reset flag when charge become valid - subMinute is needed to deal with server vs. client clock inaccuracy
    if (validSince && subMinutes(validSince, 1) <= new Date() && this.charge) {
      /*   this.charge.vehicle.canBeChangedBeforeValidity = true;
      this.charge.vehicle.canBeChangedDuringValidity = false; */
    }
  }

  @action.bound
  getUntilValidity(chargeTypeId: string, validSince: Date) {
    return this.chargeRepository.chargeValidity(chargeTypeId, validSince);
  }

  @action.bound
  getPriceListItem(priceListId: string) {
    return this.chargeTypesService.chargePriceList.items.find(item => item.id === priceListId);
  }

  @action.bound
  getCharge(countryId: string, licensePlate: string, authCode: string, turnstileToken: string) {
    return this.chargeRepository.getCharge(countryId, licensePlate, authCode, turnstileToken).then(response => {
      if (response instanceof EshopError) {
        this.eventBus.publish(chargeValidationFailed({ type: response.type }));
        return response;
      } else {
        Object.assign(response.notificationPreferences, response.notificationPreferences);
        attachAutomaticDirtyWatcher(response.notificationPreferences, true);
        response.authCode = authCode;

        this.charge = response;
        return this.charge;
      }
    });
  }

  @action.bound
  editChargeValidity = async (validSince: Date, emailAddress: string | undefined, turnstileToken: string) => {
    const countryId = this.charge?.vehicle.countryId;
    const licensePlate = this.charge?.vehicle.licensePlate;
    const authCode = this.charge?.authCode;
    let connectionId;

    try {
      if (!countryId || !licensePlate || !authCode || !validSince) {
        throw new Error("Missing some arguments");
      }
      await this.signalRNotificationService.initialize();
      this.signalRNotificationService.subscribeToPutChargeValidityResult(emailAddress);
      await this.signalRNotificationService.serviceStart();
      connectionId = this.signalRNotificationService.connectionId;

      if (!connectionId) {
        throw new Error("Connection id is not set");
      }
      if (!turnstileToken) {
        throw new Error("Missing turnstile token");
      }
    } catch (e) {
      this.eventBus.publish(putChargeValidityFailure({ type: WEBSOCKET_CONNECTION_FAILURE }));
      return;
    }

    const hasImmediateValidity = isSameOrBeforePragueDay(validSince, this.timeService.currentServerTime ?? new Date());
    const request = {
      hasImmediateValidity,
      validSinceDate: hasImmediateValidity ? undefined : validSince,
    } as ChargeValidityRequest;

    return this.chargeRepository
      .editChargeValidity(countryId, licensePlate, emailAddress, authCode, connectionId, request, turnstileToken)
      .then(response => {
        if (response) {
          this.eventBus.publish(putChargeValidityFailure({ type: response.type }));
        }
      })
      .catch(() => {
        this.eventBus.publish(putChargeValidityFailure({ type: PUT_CHARGE_VALIDITY_REQUEST_FAILURE }));
      });
  };

  @action.bound
  async getChargeOrderInstructions(newCountryId: string, newLicensePlate: string) {
    return this.vehicleRepository.getChargeOrderInstruction(newCountryId, newLicensePlate);
  }

  @action.bound
  async getChargeValidityInstructions(countryId: string, licensePlate: string, newValidSince?: Date, chargeId?: string) {
    const hasImmediateValidity = newValidSince
      ? isSameOrBeforePragueDay(newValidSince, this.timeService.currentServerTime ?? new Date())
      : false;

    return this.vehicleRepository.proposedValidity(
      countryId,
      licensePlate,
      this.charge!.priceListItemId,
      hasImmediateValidity,
      undefined,
      newValidSince ? dateToIsoDate(newValidSince) : "",
      chargeId
    );
  }

  @action.bound
  async editVehicle(newCountryId: string, newLicensePlate: string, emailAddress: string | undefined, turnstileToken: string) {
    const countryId = this.charge?.vehicle.countryId;
    const licensePlate = this.charge?.vehicle.licensePlate;
    const authCode = this.charge?.authCode;
    let connectionId;

    try {
      if (!countryId || !licensePlate || !authCode || !newCountryId || !newLicensePlate) {
        throw new Error("Missing some arguments");
      }
      await this.signalRNotificationService.initialize();
      this.signalRNotificationService.subscribeToPutChargeVehicleResult(emailAddress);
      await this.signalRNotificationService.serviceStart();
      connectionId = this.signalRNotificationService.connectionId;

      if (!connectionId) {
        throw new Error("Connection id is not set");
      }

      if (!turnstileToken) {
        throw new Error("Missing turnstile token");
      }
    } catch (e) {
      this.eventBus.publish(putChargeVehicleFailure({ type: WEBSOCKET_CONNECTION_FAILURE }));
      return;
    }

    return this.chargeRepository
      .editVehicle(countryId, licensePlate, emailAddress, authCode, connectionId, newCountryId, newLicensePlate, turnstileToken)
      .then(response => {
        if (response) {
          this.eventBus.publish(putChargeVehicleFailure({ type: response.type }));
        }
      })
      .catch(() => {
        this.eventBus.publish(putChargeVehicleFailure({ type: PUT_CHARGE_VEHICLE_REQUEST_FAILURE }));
      });
  }

  @action.bound
  async editActiveCharge(request: VehicleChangeDuringValidityRequest, turnstileToken: string) {
    const countryId = this.charge?.vehicle.countryId;
    const licensePlate = this.charge?.vehicle.licensePlate;
    const authCode = this.charge?.authCode;
    let connectionId;

    try {
      if (!countryId || !licensePlate || !authCode) {
        throw new Error("Missing some arguments");
      }
      await this.signalRNotificationService.initialize();
      this.signalRNotificationService.subscribeToPutChargeVehicleChangesDuringValidityResult(request.emailAddress);
      await this.signalRNotificationService.serviceStart();
      connectionId = this.signalRNotificationService.connectionId;

      if (!connectionId) {
        throw new Error("Connection id is not set");
      }

      if (!turnstileToken) {
        throw new Error("Missing turnstile token");
      }
    } catch (e) {
      this.eventBus.publish(putChargeVehicleFailure({ type: WEBSOCKET_CONNECTION_FAILURE }));
      return;
    }

    return this.chargeRepository
      .editActiveChargeVehicle(countryId, licensePlate, authCode, connectionId, request, turnstileToken)
      .then(response => {
        if (response) {
          this.eventBus.publish(putChargeVehicleFailure({ type: response.type }));
        }
      })
      .catch(() => {
        this.eventBus.publish(putChargeVehicleFailure({ type: PUT_CHARGE_VEHICLE_REQUEST_FAILURE }));
      });
  }

  @action.bound
  async editContact(
    notificationPreferences: NotificationPreferences,
    triggerer: ContactEditationTriggerer,
    turnstileToken: string
  ) {
    const countryId = this.charge?.vehicle.countryId;
    const licensePlate = this.charge?.vehicle.licensePlate;
    const authCode = this.charge?.authCode;
    let connectionId;

    try {
      if (!countryId || !licensePlate || !authCode) {
        throw new Error("Missing some arguments");
      }
      await this.signalRNotificationService.initialize();
      this.signalRNotificationService.subscribeToPutChargeContactResult(triggerer);
      await this.signalRNotificationService.serviceStart();
      connectionId = this.signalRNotificationService.connectionId;

      if (!connectionId) {
        throw new Error("Connection id is not set");
      }
    } catch (e) {
      this.eventBus.publish(putChargeContactFailure({ type: WEBSOCKET_CONNECTION_FAILURE, triggerer }));
      return;
    }

    const request: ContactInfoChangeRequest = {
      notificationPreferences: {
        emailNotificationEnabled: notificationPreferences.emailNotificationEnabled,
        emailAddress: notificationPreferences._emailAddressNotification,
        smsNotificationEnabled: notificationPreferences.smsNotificationEnabled,
        phoneNumber: notificationPreferences.phoneNumber,
      },
    };
    if (notificationPreferences.emailAddress && notificationPreferences.emailAddress.length > 0) {
      request.emailAddress = notificationPreferences.emailAddress;
    }
    if (!notificationPreferences._notificationEnabled) {
      request.notificationPreferences = new NotificationPreferences();
    }
    if (request.notificationPreferences?.emailAddress?.length === 0) {
      request.notificationPreferences.emailAddress = undefined;
      request.notificationPreferences.emailNotificationEnabled = false;
    }
    return this.chargeRepository
      .editContact(request, countryId, licensePlate, authCode, connectionId, triggerer, turnstileToken)
      .then(response => {
        if (response) {
          this.eventBus.publish(putChargeContactFailure({ type: response.type, triggerer }));
        }
      })
      .catch(() => {
        this.eventBus.publish(putChargeContactFailure({ type: PUT_CHARGE_CONTACT_REQUEST_FAILURE, triggerer }));
      });
  }

  @action.bound
  sendChargePaymentConfirmationEmail(emailAddress: string | undefined, turnstileToken: string) {
    const countryId = this.charge?.vehicle.countryId;
    const licensePlate = this.charge?.vehicle.licensePlate;
    const authCode = this.charge?.authCode;

    if (!countryId || !licensePlate || !authCode) {
      this.eventBus.publish(
        sendChargePaymentConfirmationEmailFailure({ type: SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE })
      );
      return;
    }

    return this.chargeRepository
      .sendChargePaymentConfirmationEmail(countryId, licensePlate, emailAddress, authCode, turnstileToken)
      .then(response => {
        if (response && response.type) {
          this.eventBus.publish(sendChargePaymentConfirmationEmailFailure({ type: response.type }));
        } else {
          this.eventBus.publish(sendChargePaymentConfirmationEmailSuccess({}));
          if (emailAddress) {
            this.eventBus.publish(emailUpdated({ emailAddress }));
          }
        }
      })
      .catch(() => {
        this.eventBus.publish(
          sendChargePaymentConfirmationEmailFailure({ type: SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE })
        );
      });
  }

  @action.bound
  downloadPaymentConfirmation = (turnstileToken: string): Promise<string | undefined> => {
    const countryId = this.charge?.vehicle.countryId;
    const licensePlate = this.charge?.vehicle.licensePlate;
    const authCode = this.charge?.authCode;

    if (!countryId || !licensePlate || !authCode) {
      this.eventBus.publish(downloadPaymentConfirmationFailure({ type: DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE }));
      return Promise.resolve(undefined);
    }

    return this.chargeRepository
      .downloadPaymentConfirmation(countryId, licensePlate, authCode, turnstileToken)
      .then(response => {
        if (response instanceof EshopError) {
          this.eventBus.publish(downloadPaymentConfirmationFailure({ type: response.type }));
        } else {
          if (response) {
            this.eventBus.publish(downloadPaymentConfirmationSuccess({}));
            return response;
          }
        }
      })
      .catch(() => {
        this.eventBus.publish(downloadPaymentConfirmationFailure({ type: DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE }));
        return undefined;
      });
  };

  setLoginChargeManagement() {
    sessionStorage.setItem(SESSION_STORAGE_CHARGE_MANAGEMENT_KEY, JSON.stringify(this.turnstileToken));
  }

  getLoginChargeManagement() {
    return sessionStorage.getItem(SESSION_STORAGE_CHARGE_MANAGEMENT_KEY);
  }

  removeLoginChargeManagement() {
    this.clearLoginChargeManagement();
  }

  async clearLoginChargeManagement() {
    sessionStorage.removeItem(SESSION_STORAGE_CHARGE_MANAGEMENT_KEY);
  }

  @action.bound
  reset() {
    this.charge = undefined;
  }
}
